import { useState } from "react";
import queryKeys from "../configs/react-query/query-key";
import { useMutationData } from "../hooks/useMutationData";

export const useSearchInfo = () => {
  const [lists, setLists] = useState({
    departmentList: [],
    verificationReportStatusList: [],
    creatorList: [],
    creatorIdList: [],
    clarityStatusList: [],
    fossIdStatusList: [],
    sbomStatusList: [],
    componentStatusList: [],
    licenseTypeList: [],
    matchTypeList: [],
    noticeStatusList: [],
    obligationTypeList: [],
    postClassList: [],
    postTypeList: [],
    qnaStatusList: [],
    roleList: [],
    assignableRolesList: [],
    scannedList: [],
    serverityList: [],
    verifyReportResultList: [],
    verifyToolList: [],
    verifyTypeList: [],
    verifyStatusList: [],
    assetClassificationImportanceLevelList: [],
    assetClassificationsList: [],
    importComponentStatusList: [],
    productsList: [],
    defaultProduct: null,
    requestStatusList: [],
    scanToolList: [],
    workTypeList: [],
    entityTypeList: [],
    projectManagerList: [],
  });

  const [isFetched, setIsFetched] = useState(false);


  const mapToOptions = (items, valueKey = "code", labelKey = "codeName", onlyGetItemHasUseFlagIsYes = false) =>
    items
      ?.filter((item) => !onlyGetItemHasUseFlagIsYes || item.useFlag === "Y")
      .map((item) => ({ value: item[valueKey], label: item[labelKey] })) || [];

  const mapCreatorsToOptions = (items) =>
    items?.map((item) => ({
      value: item.userId,
      label: item.userName,
      roleCd: item.roleCd,
      roleCdName: item.roleCdName,
      ...item, // Include all original properties
    })) || [];

  const mapCreatorIdsToOptions = (items) =>
    items?.map((item) => ({
      value: item.userId,
      label: item.userId,
      roleCd: item.roleCd,
      roleCdName: item.roleCdName,
      ...item, // Include all original properties
    })) || [];

  const mapAssetClassificationsToOptions = (isGetName, items) =>
    [...(items || [])]
      .sort((a, b) => a.assetClassificationSeq - b.assetClassificationSeq)
      .map((item) => ({
        value: item["assetClassificationSeq"],
        label: item[isGetName ? "assetClassificationName" : "importanceLevelCdName"],
      }));

  const findDefaultProduct = (products) =>
    products?.find((item) => item.defaultFlag === "Y") || null;

  const mapRequestStatusesToOptions = (statuses) => {
    if (!statuses) return [];
    
    const statusLabelMap = {
      "PENDING COMPLETION": "Pending Completion",
      "PENDING APPROVAL": "Pending Approval", 
      "DONE": "Done"
    };
    
    return statuses.map((status) => ({
      value: status,
      label: statusLabelMap[status] || status, 
    }));
  };

  const { mutate: fetchSearchInfo, isPending } = useMutationData(
    queryKeys.getSearchInfosList,
    {
      method: "GET",
    },
    {
      onSuccess: (response) => {
        if (typeof response !== "string") {
          setLists({
            departmentList: mapToOptions(response.creatorDepartments),
            verificationReportStatusList: mapToOptions(response.verifyReportStatuses),
            verifyToolList: mapToOptions(response.verifyTools),
            creatorList: mapCreatorsToOptions(response.creators),
            creatorIdList: mapCreatorIdsToOptions(response.creators),
            clarityStatusList: mapToOptions(response.clarityVerifyStatuses),
            fossIdStatusList: mapToOptions(response.fossIdVerifyStatuses),
            sbomStatusList: mapToOptions(response.sbomVerifyStatuses),
            componentStatusList: mapToOptions(response.componentStatuses),
            matchTypeList: mapToOptions(response.matchTypes),
            licenseTypeList: mapToOptions(response.licenseTypes),
            noticeStatusList: mapToOptions(response.noticeStatuses),
            obligationTypeList: mapToOptions(response.obligationTypes),
            postClassList: mapToOptions(response.postClass),
            postTypeList: mapToOptions(response.postType),
            qnaStatusList: mapToOptions(response.qnaStatus),
            roleList: mapToOptions(response.roles),
            assignableRolesList: mapToOptions(response.assignableRoles),
            scannedList: mapToOptions(response.scaned),
            serverityList: mapToOptions(response.severities),
            verifyReportResultList: mapToOptions(response.verifyReportResults),
            verifyTypeList: mapToOptions(response.verifyTypes),
            verifyStatusList: mapToOptions(response.verifyStatuses),
            assetClassificationImportanceLevelList: mapAssetClassificationsToOptions(
              false,
              response.assetClassifications,
            ),
            assetClassificationsList: mapAssetClassificationsToOptions(true, response.assetClassifications),
            importComponentStatusList: mapToOptions(response.importComponentStatus),
            requestStatusList: mapRequestStatusesToOptions(response.requestStatuses),
            scanToolList: mapToOptions(response.scanTools),
            workTypeList: mapToOptions(response.workTypes, 'code', 'name', false),
            entityTypeList: mapToOptions(response.entityTypes, 'code', 'name', false),
            productsList: mapToOptions(response.products, 'productSeq', 'productName', false),
            defaultProduct: findDefaultProduct(response.products),
            projectManagerList: mapToOptions(response.projectManagers, 'userId', 'userName', false),
          });
        };
        setIsFetched(true);
      },
      onError: (error) => {
        console.error("Error fetching search info data:", error);
        setIsFetched(true);
      },
    },
  );

  return {
    ...lists,
    fetchSearchInfo,
    isLoading: isPending,
    isFetched: isFetched
  };
};
